import { useState, useMemo, useRef, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import { ArrowLeft, RefreshCw, Loader2, X, Plus, Clock, Trash2, Mail, Check, Link2, ExternalLink, ChevronDown, History, Box, Pencil, TrendingUp, FileCheck, AlertTriangle, Presentation, CalendarDays } from 'lucide-react'
import InfoTooltip from '../components/InfoTooltip'
import { useApi, useAsanaApi } from '../hooks/useApi'
import { useAuth } from '../hooks/useAuth'
import { useData } from '../hooks/useData'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { getTierColor, formatDate, formatDateTime, formatCurrency, formatNumber, statusConfig, getInitials, isValidEmail, calculateAverageSatisfactionScore, projectAdoptionPct, forecastOnTime } from '../lib/utils'
import { TIERS } from '../lib/constants'
import { CHART_COLORS, GRID_STROKE, AXIS_TICK } from '../lib/chartOpts'
import {
  MONTH_NAMES,
  monthKey,
  getEntry,
  yearTotal,
  yearToDateTotal,
  ledgerYears,
  diffEdits,
  applyTotalEdits,
  type LedgerDiffRow,
} from '../lib/ledger'
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '../components/ui/chart'
import Field, { inputClass } from '../components/Field'
import Select from '../components/Select'
import DateInput from '../components/DateInput'
import LoadingSpinner from '../components/LoadingSpinner'
import Modal from '../components/Modal'
import { ChartCard } from '../components/ChartCard'
import ProductPicker from '../components/ProductPicker'
import Popover from '../components/Popover'
import RisksCard from '../components/RisksCard'
import type { Project, Product, AuditEntry, AdoptionMode, SurveyResponse, RiskEntry, MonthReview } from '../types'
import { effectiveRiskScore, DEFAULT_RISK_FORECAST_MIN_SCORE } from '../lib/risk'
import { currentReportMonth } from '../lib/report'
import { resolveWindow, windowChipLabel, type WindowState } from '../lib/reportingWindow'

// Keep in sync with the same list in AddProject.tsx and `AdoptionMode` in
// types/index.ts. Users = count-based (default), Direct % = self-reported,
// Not applicable = excluded from the Adoption KPI entirely.
const ADOPTION_MODE_OPTIONS = [
  ['users',   'Users (count)'],
  ['percent', 'Direct %'],
  ['na',      'Not applicable'],
] as const

function CurrencyInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [focused, setFocused] = useState(false)
  return (
    <input
      type={focused ? 'number' : 'text'}
      value={focused ? (value || '') : (value ? formatCurrency(value) : '')}
      onChange={e => onChange(Number(e.target.value))}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder="€0"
      className={inputClass}
    />
  )
}

// Small chain-link button shown on each mappable field label.
// Linked fields show a rose icon (click to detach). Unlinked fields show a grey icon (click to pick an Asana field).
function FieldLinkButton({ currentGid, customFields, onUpdate }: {
  currentGid: string | null
  customFields: any[]
  onUpdate: (gid: string | null) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const linked = !!currentGid
  const optionClass = 'w-full text-left px-3 py-2 text-sm hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none flex items-center gap-2'
  return (
    <div ref={ref} className="relative inline-flex">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={linked ? 'Change the Asana field this is linked to' : 'Link to an Asana field'}
        onClick={() => setOpen(!open)}
        title={linked ? 'Linked to Asana - click to change' : 'Click to link to an Asana field'}
        className={`flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded transition-colors ${
          linked ? 'text-[#EF3340] hover:bg-[#EF3340]/10' : 'text-slate-400 hover:bg-slate-100'
        }`}
      >
        <Link2 className="w-3 h-3" />
      </button>
      <Popover open={open} anchorRef={ref} onClose={() => setOpen(false)} width="content" className="min-w-48 max-w-xs" label="Asana fields">
        <div role="listbox" className="py-1 min-h-0 overflow-y-auto">
          <button
            type="button"
            role="option"
            aria-selected={!currentGid}
            data-option
            onClick={() => { onUpdate(null); setOpen(false) }}
            className={`${optionClass} text-slate-500`}
          >
            <span className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${!currentGid ? 'bg-danone-cyan border-danone-cyan' : 'border-slate-300'}`}>
              {!currentGid && <Check className="w-2 h-2 text-white" />}
            </span>
            Manual (no Asana link)
          </button>
          {customFields.map(cf => (
            <button
              key={cf.gid}
              type="button"
              role="option"
              aria-selected={currentGid === cf.gid}
              data-option
              onClick={() => { onUpdate(cf.gid); setOpen(false) }}
              className={`${optionClass} text-slate-700`}
            >
              <span className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center flex-shrink-0 ${currentGid === cf.gid ? 'bg-danone-cyan border-danone-cyan' : 'border-slate-300'}`}>
                {currentGid === cf.gid && <Check className="w-2 h-2 text-white" />}
              </span>
              <span className="truncate">{cf.name}</span>
              <span className="ml-auto text-[10px] text-slate-400 flex-shrink-0">{cf.type}</span>
            </button>
          ))}
          {customFields.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-400">No Asana fields available</p>
          )}
        </div>
      </Popover>
    </div>
  )
}


export default function ProjectDetail() {
  const api = useApi()
  const asanaApi = useAsanaApi()
  const { user: authUser } = useAuth()
  const isAdmin = !!authUser?.isAdmin
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, loading } = useData(() =>
    Promise.all([api.getProject(id!), api.getSurveyResponses(id), api.getSettings(), api.getFieldMappings(), api.getProducts()])
      .then(([project, responses, settings, mappings, products]) => ({ project, responses, settings, mappings, products })),
    [id]
  )
  const [project, setProject] = useState<Project | null>(null)
  useDocumentTitle(project?.name)
  const [liveMapping, setLiveMapping] = useState<Record<string, string | null> | null>(null)
  const [customFields, setCustomFields] = useState<any[]>([])
  const [syncing, setSyncing] = useState(false)
  // Whether the current user has a working Asana token. Used to disable the
  // Sync button (and explain why) instead of letting it fail with a generic
  // red error after the click.
  const [asanaConnected, setAsanaConnected] = useState<boolean | null>(null)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [errorModal, setErrorModal] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [sendingEmail, setSendingEmail] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [showSystemAudit, setShowSystemAudit] = useState(false)
  const [auditOpen, setAuditOpen] = useState(false)

  // Monthly ledger state
  const [ledgerYear, setLedgerYear] = useState<number>(new Date().getFullYear())
  const [ledgerEditing, setLedgerEditing] = useState(false)
  const [editBuffer, setEditBuffer] = useState<Record<string, number>>({})
  const [ledgerReview, setLedgerReview] = useState<LedgerDiffRow[] | null>(null)
  const [ledgerNote, setLedgerNote] = useState('')
  const [savingLedger, setSavingLedger] = useState(false)
  const [refreshingForecast, setRefreshingForecast] = useState(false)
  const [refreshConfirm, setRefreshConfirm] = useState(false)
  const [users, setUsers] = useState<{ oid: string; name?: string; email?: string }[]>([])
  const [showOwnerPicker, setShowOwnerPicker] = useState(false)
  const [ownerQuery, setOwnerQuery] = useState('')
  const ownerPickerRef = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (isAdmin) api.getUsers().then((u: any[]) => setUsers(u)).catch(() => {})
  }, [isAdmin])

  // Probe Asana connection so we can grey out Sync Now (and explain why) when
  // the user hasn't connected their Asana account yet, instead of letting them
  // click and see a generic 401.
  useEffect(() => {
    if (!project?.asanaProjectId) return
    let cancelled = false
    asanaApi.me().then(u => { if (!cancelled) setAsanaConnected(!!u) }).catch(() => { if (!cancelled) setAsanaConnected(false) })
    return () => { cancelled = true }
  }, [asanaApi, project?.asanaProjectId])

  // The report link carries this month's deadline, so a project manager sees
  // it before opening the editor. Only fetched for projects that file one.
  const [reportWindow, setReportWindow] = useState<WindowState | null>(null)
  useEffect(() => {
    if (!project?.reportable) return
    const month = currentReportMonth()
    let cancelled = false
    api.getMonthReview(month)
      .then((review: MonthReview) => { if (!cancelled) setReportWindow(resolveWindow(month, review, project.id)) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [project?.id, project?.reportable])



  useEffect(() => {
    if (!data) return
    const d = data as any
    // getProject() returns null on 404. Also normalise `stakeholders` to an
    // array so older docs / partial writes can't crash the page.
    const fetched = d.project && d.project.id ? { ...d.project, stakeholders: d.project.stakeholders || [] } : null
    setProject(fetched)
    const m = (d.mappings || []).find((m: any) => fetched && m.projectId === fetched.id)
    if (m) setLiveMapping(m.mappings)
  }, [data])

  // Ledger computed values - must be above conditional returns since they use hooks.
  const { ytdHrs, thisYearHrs, lifetimeHrs } = useMemo(() => {
    if (!project) return { ytdHrs: 0, thisYearHrs: 0, lifetimeHrs: 0 }
    const now = new Date()
    const curYear = now.getFullYear()
    const curMonth = now.getMonth() + 1
    const years = new Set<number>()
    for (const key of Object.keys(project.monthlyLedger || {})) {
      const y = Number(key.slice(0, 4))
      if (!Number.isNaN(y)) years.add(y)
    }
    let lifetime = 0
    for (const y of years) {
      if (y < curYear) lifetime += yearTotal(project.monthlyLedger, y)
      else if (y === curYear) lifetime += yearToDateTotal(project.monthlyLedger, y, curMonth)
      // future years are not realised
    }
    return {
      ytdHrs: yearToDateTotal(project.monthlyLedger, curYear, curMonth),
      thisYearHrs: yearTotal(project.monthlyLedger, curYear),
      lifetimeHrs: lifetime,
    }
  }, [project])

  const years = project ? ledgerYears(project.monthlyLedger) : [new Date().getFullYear()]
  const activeYear = years.includes(ledgerYear) ? ledgerYear : years[years.length - 1]

  const monthRows = useMemo(() => {
    const now = new Date()
    const nowYear = now.getFullYear()
    const nowMonth = now.getMonth() + 1
    return Array.from({ length: 12 }, (_, i) => {
      const m = i + 1
      const key = monthKey(activeYear, m)
      const entry = getEntry(project?.monthlyLedger, key)
      const savedTotal = entry.base + entry.adjustments
      const edited = editBuffer[key]
      const currentTotal = edited !== undefined ? edited : savedTotal
      // A month is "actual" if it's in a past year, or current year and month <= now
      const isActual = activeYear < nowYear || (activeYear === nowYear && m <= nowMonth)
      return {
        key,
        label: MONTH_NAMES[i],
        base: entry.base,
        deltaVsBase: currentTotal - entry.base,
        total: currentTotal,
        notes: entry.adjustmentNotes,
        dirty: edited !== undefined && Math.round(edited) !== Math.round(savedTotal),
        isActual,
      }
    })
  }, [project, activeYear, editBuffer])

  if (loading || !data) return <LoadingSpinner />
  if (!project) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center">
        <h1 className="text-xl font-bold text-slate-800 mb-2">Project not found</h1>
        <p className="text-slate-500 mb-6">It may have been deleted or the link is wrong.</p>
        <button onClick={() => navigate('/projects')} className="btn-primary">Back to Projects</button>
      </div>
    )
  }

  const { responses, settings, mappings: allMappings, products } = data as { project: Project; responses: any[]; settings: any; mappings: any[]; products: Product[] }
  // Resolve the linked product (if any) so the header can show a clickable
  // chip - keeps the Project ↔ Product relationship visible from both sides.
  const linkedProduct = project.productId ? (products || []).find(p => p.id === project.productId) : undefined
  const mapping = (allMappings || []).find((m: any) => m.projectId === project.id)
  const activeMappings = liveMapping ?? mapping?.mappings ?? {}
  const asanaFields = new Set(Object.entries(activeMappings).filter(([, v]) => v).map(([k]) => k))
  const isAsana = (field: string) => asanaFields.has(field)
  const updateField = (field: keyof Project, value: any) => { setProject({ ...project, [field]: value }); setDirty(true) }

  // Persisted on click rather than routed through the page's dirty/Save flow.
  // A switch reads as applied the moment it moves, and the report link appears
  // right beneath it - routing this through Save Changes would lose the
  // setting for anyone who toggles on and goes straight into the report.
  const toggleReportType = async (field: 'reportable' | 'ddi', next: boolean) => {
    setProject({ ...project, [field]: next })
    try {
      await api.updateProject(project.id, { [field]: next })
    } catch (e) {
      setProject({ ...project, [field]: !next })
      setErrorModal(e instanceof Error ? e.message : 'Could not change the reporting setting.')
    }
  }

  // Load Asana custom fields on demand (once per project detail view)
  if (project.asanaProjectId && customFields.length === 0) {
    asanaApi.getProject(project.asanaProjectId).then(p => setCustomFields(p.custom_fields || [])).catch(() => {})
  }

  const handleFieldLink = async (fieldKey: string, gid: string | null) => {
    if (!mapping) return
    const newMappings = { ...activeMappings, [fieldKey]: gid }
    setLiveMapping(newMappings)
    await api.updateFieldMapping(mapping.id, { mappings: newMappings })

    // Immediately apply the new Asana field value so the UI updates without needing a sync
    if (gid && gid !== 'due_on') {
      const cf = customFields.find((c: any) => c.gid === gid)
      if (cf) {
        // extractCustomFieldValue logic inline (can't import here)
        let value: any = null
        if (cf.type === 'number' && cf.number_value != null) value = cf.number_value
        else if (cf.type === 'date') value = cf.date_value?.date ?? null
        else if (cf.enum_value) value = cf.enum_value.name
        else if (cf.display_value) value = cf.display_value
        if (value !== null) {
          setProject(p => p ? { ...p, [fieldKey]: value } : p)
          await api.updateProject(project.id, { [fieldKey]: value })
        }
      }
    }
  }

  const handleSync = async () => {
    // Both prerequisites are checked separately so the user sees *why* nothing
    // happens. Silently returning here was a real foot-gun: a missing field
    // mapping made the button no-op with no spinner, no modal, nothing.
    if (!project.asanaProjectId) {
      setErrorModal('This project is not linked to an Asana project. Link it from the Asana section first.')
      return
    }
    if (asanaConnected === false) {
      setErrorModal('You are not connected to Asana. Open Settings and connect your Asana account, then try again.')
      return
    }
    if (!mapping) {
      setErrorModal('No field mapping configured for this project. Open the field mapping panel and map at least one Asana field before syncing.')
      return
    }
    setSyncing(true)
    try {
      // Backend writes synced fields with SYSTEM_ACTOR when projectId is passed,
      // so the audit trail captures the change.
      const synced = await asanaApi.syncProject(project.asanaProjectId, mapping.mappings, project.id)
      setProject({ ...project, ...synced })
    } catch (e: any) {
      setErrorModal(e?.message || 'Failed to sync with Asana')
    }
    setSyncing(false)
  }

  const handleSave = async () => {
    // Local guard: status='complete' requires actualGoLive. The backend seeds
    // the project's own monthly ledger from expectedValue on completion.
    if (project.status === 'complete' && !project.actualGoLive) {
      setErrorModal('Cannot mark as Complete without an Actual Go-Live date. Enter the go-live date first, or keep the project as Forecast.')
      return
    }
    setSaving(true)
    try {
      // JSON.stringify drops undefined values, so explicitly null out optional
      // fields that have been cleared so the backend removes them from the doc.
      const payload = { ...project, productId: project.productId ?? null }
      await api.updateProject(project.id, payload)
      setDirty(false)
    } catch (e: any) {
      setErrorModal(e?.message || 'Failed to save project')
    }
    setSaving(false)
  }


  // Risks save immediately (each add/edit/status change is a discrete action,
  // like ledger edits) rather than riding the header Save button. Optimistic
  // update with rollback on failure.
  const saveRisks = async (risks: RiskEntry[]) => {
    const prev = project.risks
    setProject({ ...project, risks })
    try {
      await api.updateProject(project.id, { risks })
    } catch (e: any) {
      setProject(p => p ? { ...p, risks: prev } : p)
      setErrorModal(e?.message || 'Failed to save risks')
    }
  }

  const handleDelete = async () => {
    await api.deleteProject(project.id)
    navigate('/projects')
  }

  // ─── Ledger edit handlers ───────────────────────────────────────────────
  const hasLedger = project.status === 'complete' || Object.keys(project.monthlyLedger || {}).length > 0
  const activeYearTotal = monthRows.reduce((s, r) => s + r.total, 0)
  const trendData = monthRows.map(r => ({ month: r.label, hrs: Math.round(r.total) }))
  const trendConfig: ChartConfig = { hrs: { label: 'Hrs Saved', color: CHART_COLORS.cyan } }

  const startLedgerEdit = () => {
    setEditBuffer({})
    setLedgerEditing(true)
  }
  const cancelLedgerEdit = () => {
    setEditBuffer({})
    setLedgerEditing(false)
    setLedgerReview(null)
    setLedgerNote('')
  }
  const setTotalFor = (key: string, raw: string) => {
    if (raw === '' || raw === '-') { setEditBuffer(b => ({ ...b, [key]: 0 })); return }
    const n = Number(raw)
    if (Number.isFinite(n)) setEditBuffer(b => ({ ...b, [key]: n }))
  }
  const openReview = () => {
    const diff = diffEdits(project.monthlyLedger, editBuffer)
    if (diff.length === 0) { setLedgerEditing(false); setEditBuffer({}); return }
    setLedgerReview(diff)
  }
  const confirmLedgerSave = async () => {
    if (!ledgerReview) return
    setSavingLedger(true)
    try {
      const nextLedger = applyTotalEdits(project.monthlyLedger, editBuffer, ledgerNote)
      const updated = await api.updateProject(project.id, { monthlyLedger: nextLedger })
      if (updated) setProject(updated)
      setLedgerReview(null)
      setLedgerEditing(false)
      setEditBuffer({})
      setLedgerNote('')
    } catch (e: any) {
      setErrorModal(e?.message || 'Failed to save ledger changes')
    }
    setSavingLedger(false)
  }
  const runRefreshForecast = async () => {
    setRefreshingForecast(true)
    try {
      const updated = await api.refreshForecastYears(project.id)
      if (updated) setProject(updated)
      setRefreshConfirm(false)
    } catch (e: any) {
      setRefreshConfirm(false)
      setErrorModal(e?.message || 'Failed to refresh forecast years')
    }
    setRefreshingForecast(false)
  }

  const hasSent = !!project.surveySentAt

  // For resends: figure out which stakeholders haven't responded yet by
  // looking up each stakeholder's per-recipient token on the project doc
  // (populated by the backend on first send) and asking the server whether
  // that token has a submission hash. A stakeholder without a token in the
  // map has never been sent a link → definitely hasn't responded.
  const getNotResponded = async (stakeholders: string[]) => {
    const tokens = project.stakeholderTokens ?? {}
    const checks = await Promise.all(
      stakeholders.map(async email => {
        const recipientToken = tokens[email]
        if (!recipientToken) return { email, submitted: false }
        try {
          const result = await api.checkSurveySubmitted(project.surveyToken, recipientToken)
          return { email, submitted: result.submitted }
        } catch {
          // If the server can't check (404, network), fall back to treating
          // them as not-responded so the resend button still works.
          return { email, submitted: false }
        }
      }),
    )
    return checks.filter(c => !c.submitted).map(c => c.email)
  }

  const doSend = async (stakeholders: string[], setSending: (v: boolean) => void, onDone: () => void) => {
    setSending(true)
    let sendResult: { stakeholderTokens?: Record<string, string> } = {}
    try {
      sendResult = await api.sendSurvey({
        projectId: project.id,
        projectName: project.name,
        surveyToken: project.surveyToken,
        stakeholders,
        origin: window.location.origin,
      })
    } catch (err: any) {
      setErrorModal(err?.message || 'Failed to send survey')
      setSending(false)
      return
    }
    const surveyEmailsSent = [...new Set([...(project.surveyEmailsSent ?? []), ...stakeholders])]
    const surveySentAt = new Date().toISOString()
    // Backend may have generated new per-recipient tokens - merge them into
    // local state so the next resend can ask the server "did this person
    // respond?" without a full project re-fetch.
    const stakeholderTokens = sendResult.stakeholderTokens ?? project.stakeholderTokens ?? {}
    await api.updateProject(project.id, { surveySentAt, surveyEmailsSent })
    setProject({ ...project, surveySentAt, surveyEmailsSent, stakeholderTokens })
    setDirty(false)
    onDone()
    setSending(false)
  }

  const handleSendSurvey = async () => {
    setSending(true)
    const pending = hasSent ? await getNotResponded(project.stakeholders) : project.stakeholders
    if (pending.length === 0) { setSending(false); return }
    await doSend(pending, setSending, () => {})
  }

  const handleSendToOne = async (email: string) => {
    setSendingEmail(email)
    await doSend([email], () => {}, () => setSendingEmail(null))
  }

  const addStakeholder = () => {
    if (!newEmail.trim()) return
    if (!isValidEmail(newEmail.trim())) {
      setErrorModal('Please enter a valid email address')
      return
    }
    updateField('stakeholders', [...project.stakeholders, newEmail.trim()])
    setNewEmail('')
  }

  const removeStakeholder = (email: string) => {
    updateField('stakeholders', project.stakeholders.filter(e => e !== email))
  }


  const responseCount = responses.length

  const isRgm = project.subPortfolio === 'RGM'
  const adoptionMode = project.adoptionMode || 'users'
  // `skipStakeholders` is the opt-out for back-loaded projects. When set, the
  // stakeholders requirement is dropped from every downstream check and the
  // survey flow is hidden entirely (see the Survey card + pipeline below).
  const skipStakeholders = !!project.skipStakeholders
  const hasStakeholders = project.stakeholders.length > 0
  const stakeholdersSatisfied = skipStakeholders || hasStakeholders
  const missingFields = [
    !project.actualGoLive && 'Go-Live Date',
    !project.deliveredInFull && 'Delivered in Full',
    adoptionMode === 'users' && !project.expectedUsers && 'Expected Users',
  ].filter(Boolean) as string[]
  const configDone = stakeholdersSatisfied && missingFields.length === 0
  const configWarn = !configDone

  // Each pipeline step can be: done (green ✓), warn (amber !), active (blue ◉), or pending (grey ○).
  // Only one of these flags should be true at a time.
  type PipelineStep = { label: string; detail?: string; done?: boolean; warn?: boolean; active?: boolean }
  const configDetail = configDone
    ? skipStakeholders
      ? 'Survey skipped for this project'
      : `${project.stakeholders.length} stakeholder${project.stakeholders.length !== 1 ? 's' : ''} added`
    : `Missing: ${[...missingFields, ...(!stakeholdersSatisfied ? ['Stakeholders'] : [])].join(', ')}`
  const pipelineSteps: PipelineStep[] = [
    { label: project.asanaProjectId ? 'Synced from Asana' : 'Project created', detail: project.lastSyncedAt ? formatDate(project.lastSyncedAt) : 'Manual entry', done: true },
    {
      label: configDone ? 'Configuration complete' : 'Needs configuration',
      detail: configDetail,
      done: configDone,
      warn: configWarn,
    },
    { label: 'Go-live', detail: project.actualGoLive ? formatDate(project.actualGoLive) : 'Not yet', done: !!project.actualGoLive && configDone },
    {
      label: 'Survey',
      detail: skipStakeholders
        ? 'Skipped'
        : !configDone
          ? 'Complete configuration first'
          : hasSent
            ? `Sent ${formatDateTime(project.surveySentAt!)}`
            : 'Ready to send',
      done: skipStakeholders || (hasSent && configDone),
      active: !skipStakeholders && configDone && !hasSent,
    },
    skipStakeholders
      ? { label: 'Responses collected', detail: 'Skipped', done: true }
      : { label: 'Responses collected', detail: `${responseCount} of ${project.stakeholders.length} received`, done: responseCount > 0 && responseCount >= project.stakeholders.length, active: responseCount > 0 && responseCount < project.stakeholders.length },
  ]

  const FIELD_TYPES: Record<string, string> = {
    subPortfolio: 'text', tier: 'text', dueDate: 'date', actualGoLive: 'date',
    forecastCost: 'number', actualCost: 'number', deliveredInFull: 'enum',
    expectedValue: 'number', adoptionRate: 'number',
  }
  const linkBtn = (fieldKey: string) => {
    if (!project.asanaProjectId) return null
    const type = FIELD_TYPES[fieldKey]
    const fields = customFields.filter(cf => cf.type === type)
    const dateExtra = type === 'date' && fieldKey === 'dueDate'
      ? [{ gid: 'due_on', name: 'Due Date (project)', type: 'date' }]
      : []
    return <FieldLinkButton currentGid={activeMappings[fieldKey] ?? null} customFields={[...dateExtra, ...fields]} onUpdate={gid => handleFieldLink(fieldKey, gid)} />
  }

  return (
    <div>
      <Modal open={!!errorModal} title="Error" message={errorModal ?? ''} variant="danger" onConfirm={() => setErrorModal(null)} />
      <Modal open={deleteConfirm} title="Delete Project" message={`Delete "${project.name}"? This cannot be undone.`} variant="danger" confirmLabel="Delete" cancelLabel="Cancel" onConfirm={() => { setDeleteConfirm(false); handleDelete() }} onCancel={() => setDeleteConfirm(false)} />
      <Modal
        open={refreshConfirm}
        title="Refresh forecast years?"
        message="This rewrites the forecast for the current and future years based on the last full year of data. Your manual adjustments on forecast months stay. Historic years (before last year) are not touched."
        confirmLabel={refreshingForecast ? 'Refreshing…' : 'Refresh'}
        cancelLabel="Cancel"
        onConfirm={runRefreshForecast}
        onCancel={() => setRefreshConfirm(false)}
      />
      <Link to="/projects" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Projects
      </Link>

      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-slate-800">{project.name}</h1>
            <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${getTierColor(project.tier)}`}>{project.tier}</span>
            {project.asanaProjectId && (
              <a href={`https://app.asana.com/0/${project.asanaProjectId}/list`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#EF3340] transition-colors">
                <span className="w-2 h-2 bg-[#EF3340] rounded-full" /> From Asana <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {project.templateMatch === true && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full" title="Created from Project Delivery template">
                <FileCheck className="w-3 h-3" /> PD Template
              </span>
            )}
          </div>
          <p className="text-slate-500 text-sm flex items-center gap-2 flex-wrap">
            <span className="inline-block px-2.5 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded-full">{project.subPortfolio}</span>
            {project.team && <span className="inline-block px-2.5 py-0.5 bg-[#005EB8]/10 text-[#005EB8] text-xs font-semibold rounded-full">{project.team}</span>}
            <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${statusConfig[project.status]?.style}`}>{statusConfig[project.status]?.label}</span>
            {linkedProduct && (
              <Link
                to={`/products/${linkedProduct.id}`}
                className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-[#00ACED]/10 text-[#0077A8] text-xs font-semibold rounded-full hover:bg-[#00ACED]/20 transition-colors"
                title={linkedProduct.vendor ? `${linkedProduct.name} · ${linkedProduct.vendor}` : linkedProduct.name}
              >
                <Box className="w-3 h-3" />
                {linkedProduct.name}
              </Link>
            )}
            {project.createdBy?.name && (
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 relative">
                <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-[9px]">
                  {getInitials(project.createdBy.email || project.createdBy.name)}
                </span>
                Created by {project.createdBy.name}
                {isAdmin && (
                  <>
                    <span ref={ownerPickerRef} className="inline-flex">
                      <button onClick={() => { setShowOwnerPicker(!showOwnerPicker); setOwnerQuery('') }}
                              aria-haspopup="listbox" aria-expanded={showOwnerPicker} aria-label="Change owner"
                              className="text-slate-400 hover:text-danone-blue" title="Change owner">
                        <Pencil className="w-3 h-3" />
                      </button>
                    </span>
                    <Popover open={showOwnerPicker} anchorRef={ownerPickerRef} onClose={() => setShowOwnerPicker(false)} width="content" className="w-64" label="Change owner">
                      <input
                        autoFocus
                        data-popover-search
                        value={ownerQuery}
                        onChange={e => setOwnerQuery(e.target.value)}
                        placeholder="Search by name or email"
                        aria-label="Search by name or email"
                        className="flex-shrink-0 px-3 py-2 text-xs border-b border-slate-100 outline-none placeholder:text-slate-400"
                      />
                      <div role="listbox" className="py-1 max-h-64 min-h-0 overflow-y-auto">
                        {users
                          .filter(u => !ownerQuery.trim() || `${u.name ?? ''} ${u.email ?? ''}`.toLowerCase().includes(ownerQuery.trim().toLowerCase()))
                          .map(u => (
                            <button
                              key={u.oid}
                              role="option"
                              aria-selected={u.oid === project.createdBy?.oid}
                              data-option
                              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 focus-visible:bg-slate-100 focus:outline-none flex items-center gap-2"
                              onClick={() => {
                                updateField('createdBy', { oid: u.oid, name: u.name, email: u.email })
                                setShowOwnerPicker(false)
                              }}
                            >
                              <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-[9px] flex-shrink-0">
                                {getInitials(u.email || u.name || '')}
                              </span>
                              <span className="truncate">{u.name || u.email}</span>
                            </button>
                          ))}
                        {users.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No users found</div>}
                      </div>
                    </Popover>
                  </>
                )}
              </span>
            )}
            {project.lastSyncedAt && <span className="text-slate-400">Last synced {formatDateTime(project.lastSyncedAt)}</span>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setDeleteConfirm(true)} className="text-red-400 hover:text-[#EF3340] px-2 py-2 rounded-lg text-sm" title="Delete project">
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('/projects')} className="bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={saving || !dirty} className="btn-primary flex items-center gap-2 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {saving ? 'Saving...' : dirty ? 'Save Changes' : 'Saved'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-4">Project Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Project Name" className="col-span-2">
                <input type="text" value={project.name} onChange={e => updateField('name', e.target.value)} className={inputClass} />
              </Field>
              <Field label="Description" className="col-span-2">
                <textarea
                  value={project.description || ''}
                  onChange={e => updateField('description', e.target.value)}
                  className={`${inputClass} min-h-[72px] resize-y`}
                  placeholder="What this initiative delivers (shown on the Scorecard)"
                />
              </Field>
              <Field label="Sub-Portfolio">
                <Select value={project.subPortfolio} onChange={v => updateField('subPortfolio', v)} options={settings?.subPortfolios ?? []} className="w-full" searchable />
              </Field>
              <Field label="Team">
                <Select value={project.team || ''} onChange={v => updateField('team', v || undefined)} options={settings?.teams ?? []} placeholder="No team" className="w-full" />
              </Field>
              <Field label="Tier">
                <Select value={project.tier} onChange={v => updateField('tier', v)} options={[...TIERS]} className="w-full" />
              </Field>
              <Field label="Status">
                <Select
                  value={project.status}
                  onChange={v => updateField('status', v)}
                  options={[['forecast', 'Forecast'], ['complete', 'Complete'], ['cancelled', 'Cancelled']]}
                  className="w-full"
                />
              </Field>
              <Field label="Product">
                <ProductPicker
                  value={project.productId}
                  onChange={v => updateField('productId', v)}
                  products={products || []}
                />
              </Field>
              <Field label="Due Date" asana={isAsana('dueDate')} action={linkBtn('dueDate')}
                info={<><strong>Due Date</strong><p>The delivery date you first agreed with the business - your target. It stays fixed as the plan evolves, so On Time can be judged against it.</p></>}><DateInput value={project.dueDate} onChange={v => updateField('dueDate', v)} disabled={isAsana('dueDate')} /></Field>
              <Field label="Go-Live Date" asana={isAsana('actualGoLive')} needed={!project.actualGoLive && !isAsana('actualGoLive')} action={linkBtn('actualGoLive')}
                info={<><strong>Go-Live Date</strong><p>While the project is <strong>Forecast</strong>, this is your current forecast go-live date - keep it updated as the plan changes.</p><p>Once the project is <strong>Complete</strong>, it becomes the actual go-live date. On Time compares this against the Due Date.</p></>}><DateInput value={project.actualGoLive || ''} onChange={v => updateField('actualGoLive', v)} disabled={isAsana('actualGoLive')} /></Field>
              <Field label="Forecast Cost (€)" asana={isAsana('forecastCost')} action={linkBtn('forecastCost')}>
                {isAsana('forecastCost')
                  ? <div className={`${inputClass} bg-slate-50 text-slate-400`}>{project.forecastCost ? formatCurrency(project.forecastCost) : '-'}</div>
                  : <CurrencyInput value={project.forecastCost} onChange={v => updateField('forecastCost', v)} />}
              </Field>
              <Field label="Actual Cost (€)" asana={isAsana('actualCost')} action={linkBtn('actualCost')}>
                {isAsana('actualCost')
                  ? <div className={`${inputClass} bg-slate-50 text-slate-400`}>{project.actualCost ? formatCurrency(project.actualCost) : '-'}</div>
                  : <CurrencyInput value={project.actualCost || 0} onChange={v => updateField('actualCost', v)} />}
              </Field>
              {project.actualCost !== undefined && project.forecastCost > 0 && (() => {
                const atCostPct = settings?.atCostPct ?? 10
                const overspendPct = Math.round(((project.actualCost - project.forecastCost) / project.forecastCost) * 100)
                const atCost = project.actualCost <= project.forecastCost * (1 + atCostPct / 100)
                const underBudget = project.actualCost <= project.forecastCost
                const label = underBudget
                  ? `${Math.abs(overspendPct)}% under budget`
                  : atCost
                    ? `${overspendPct}% over - within ${atCostPct}% tolerance`
                    : `${overspendPct}% over - exceeds ${atCostPct}% tolerance`
                const cls = underBudget ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : atCost ? 'bg-amber-50 border-amber-200 text-amber-700'
                  : 'bg-red-50 border-red-200 text-red-700'
                return (
                  <div className={`col-span-2 flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold ${cls}`}>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${underBudget ? 'bg-emerald-500' : atCost ? 'bg-amber-400' : 'bg-red-500'}`} />
                    {label}
                    <span className="ml-auto font-normal text-xs opacity-70">
                      Forecast: {formatCurrency(project.forecastCost)} · Actual: {formatCurrency(project.actualCost)}
                    </span>
                  </div>
                )
              })()}
              <Field label="Delivered in Full" needed={!project.deliveredInFull}>
                <Select value={project.deliveredInFull || ''} onChange={v => updateField('deliveredInFull', v)} options={[['Y', 'Yes'], ['N', 'No']]} placeholder="Not set" className="w-full" />
              </Field>
              <Field label="Adoption Measured By">
                <Select
                  value={project.adoptionMode || 'users'}
                  onChange={v => updateField('adoptionMode', v as AdoptionMode)}
                  options={ADOPTION_MODE_OPTIONS}
                  className="w-full"
                />
              </Field>
              {(project.adoptionMode || 'users') !== 'na' && (
                <Field label="Expected Users" needed={adoptionMode === 'users' && !project.expectedUsers}><input type="number" value={project.expectedUsers || ''} onChange={e => updateField('expectedUsers', Number(e.target.value))} className={inputClass} placeholder={adoptionMode === 'percent' ? 'Optional (weighting)' : 'Total user base'} /></Field>
              )}
              <Field label="Expected Value (Annual Hrs)" asana={isAsana('expectedValue')} action={linkBtn('expectedValue')}
                info={<><strong>Expected Value (Annual Hrs)</strong><p>Hours saved per year once live. On completion this seeds the monthly ledger (annual value / 12, prorated from the go-live month).</p></>}><input type="number" value={project.expectedValue || ''} onChange={e => updateField('expectedValue', Number(e.target.value))} className={inputClass} placeholder="Optional" disabled={isAsana('expectedValue')} /></Field>
              {/* ── Value split across recipient portfolios ── */}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-600">Hours Saved: Value Recipients</label>
                  <div className="flex items-center gap-3">
                    {isAdmin && (settings?.subPortfolios?.length ?? 0) > 0 && (
                      <button
                        type="button"
                        title="Admin only - split evenly across every sub-portfolio (sums to 100%)"
                        onClick={() => {
                          const ports = settings?.subPortfolios ?? []
                          if (!ports.length) return
                          // Integer split that sums to exactly 100: base each, then
                          // hand the remainder to the first few so the total is 100.
                          const base = Math.floor(100 / ports.length)
                          const remainder = 100 - base * ports.length
                          updateField('valueSubPortfolios', Object.fromEntries(
                            ports.map((p: string, i: number) => [p, base + (i < remainder ? 1 : 0)]),
                          ))
                        }}
                        className="text-xs font-semibold text-[#005EB8] hover:text-[#004a93]"
                      >
                        Distribute evenly
                      </button>
                    )}
                    {!project.valueSubPortfolios && (
                      <button
                        type="button"
                        onClick={() => updateField('valueSubPortfolios', { [project.subPortfolio]: 100 })}
                        className="text-xs font-semibold text-[#005EB8] hover:text-[#004a93]"
                      >
                        + Add split
                      </button>
                    )}
                  </div>
                </div>
                {project.valueSubPortfolios ? (() => {
                  const splits = project.valueSubPortfolios!
                  const total = Object.values(splits).reduce((s, v) => s + (v || 0), 0)
                  const portfolioOpts = settings?.subPortfolios ?? []
                  const usedPorts = Object.keys(splits)
                  const availablePorts = portfolioOpts.filter((p: string) => !usedPorts.includes(p))
                  return (
                    <div className="space-y-1.5">
                      {Object.entries(splits).map(([port, pct]) => (
                        <div key={port} className="flex items-center gap-2">
                          <Select
                            value={port}
                            onChange={newPort => {
                              if (newPort === port) return
                              const next = Object.fromEntries(Object.entries(splits).map(([k, v]) => [k === port ? newPort : k, v]))
                              updateField('valueSubPortfolios', next)
                            }}
                            options={[port, ...availablePorts]}
                            className="flex-1"
                          />
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={pct ?? ''}
                            onChange={e => updateField('valueSubPortfolios', { ...splits, [port]: Number(e.target.value) })}
                            className={`${inputClass} w-20 text-right`}
                            placeholder="%"
                          />
                          <span className="text-xs text-slate-400">%</span>
                          <button
                            type="button"
                            onClick={() => {
                              const next = Object.fromEntries(Object.entries(splits).filter(([k]) => k !== port))
                              updateField('valueSubPortfolios', Object.keys(next).length ? next : undefined)
                            }}
                            className="text-slate-300 hover:text-red-400 text-sm font-bold"
                          >×</button>
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-0.5">
                        <div className={`text-xs font-semibold ${total === 100 ? 'text-emerald-600' : 'text-red-500'}`}>
                          Total: {total}% {total === 100 ? '✓' : '- must equal 100%'}
                        </div>
                        {availablePorts.length > 0 && (
                          <button
                            type="button"
                            onClick={() => updateField('valueSubPortfolios', { ...splits, [availablePorts[0]]: 0 })}
                            className="text-xs font-semibold text-[#005EB8] hover:text-[#004a93]"
                          >
                            + Add portfolio
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })() : (
                  <div className="text-xs text-slate-400 px-3 py-2 bg-slate-50 rounded-lg border border-slate-100">
                    100% → <span className="font-semibold text-slate-600">{project.subPortfolio}</span> (all hours attributed to the project's sub-portfolio)
                  </div>
                )}
              </div>
              {project.subPortfolio === 'RGM' && <>
                <Field label="Trade Spend Reduction (€)"><input type="number" value={project.tradeSpendReduction || ''} onChange={e => updateField('tradeSpendReduction', Number(e.target.value))} className={inputClass} placeholder="Optional" /></Field>
                <Field label="Growth (€)"><input type="number" value={project.growth || ''} onChange={e => updateField('growth', Number(e.target.value))} className={inputClass} placeholder="Optional" /></Field>
              </>}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-4">KPI Data</h3>
            <div className="grid grid-cols-2 gap-4">
              {(project.adoptionMode || 'users') === 'users' && (
                <Field label="Actual Users">
                  <input type="number" placeholder="Headcount" value={project.actualUsers || ''}
                    onChange={e => updateField('actualUsers', Number(e.target.value))}
                    className={inputClass} />
                </Field>
              )}
              {project.adoptionMode === 'percent' && (
                <Field label="Adoption Rate (%)" asana={isAsana('adoptionRate')} action={linkBtn('adoptionRate')}>
                  <input type="number" min={0} max={100} placeholder="0-100" value={project.adoptionRate ?? ''}
                    onChange={e => updateField('adoptionRate', Number(e.target.value))}
                    className={inputClass} disabled={isAsana('adoptionRate')} />
                </Field>
              )}
            </div>
          </div>

          {!skipStakeholders && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-slate-800">Survey Responses</h3>
              <span className="text-sm text-slate-500">{responseCount} of {project.stakeholders.length} responded</span>
            </div>
            {project.stakeholders.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">Add stakeholders to start collecting responses</p>
            ) : responseCount < 5 ? (
              <div>
                <div className="text-center py-3 mb-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="text-2xl font-bold text-slate-800 mb-1">{responseCount} / {project.stakeholders.length}</div>
                  <p className="text-xs text-slate-400">Individual status hidden until 5 responses received</p>
                </div>
                {hasSent && (
                  <div className="space-y-2">
                    {project.stakeholders.map(email => (
                      <div key={email} className="flex items-center justify-between rounded-xl px-4 py-3 border border-slate-200 bg-slate-50">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-xs">{getInitials(email)}</div>
                          <span className="text-sm text-slate-700">{email}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {project.stakeholders.map(email => (
                  <div key={email} className="flex items-center justify-between rounded-xl px-4 py-3 border border-slate-200 bg-slate-50">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-xs">
                        {getInitials(email)}
                      </div>
                      <span className="text-sm text-slate-700">{email}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(() => {
              const comments = responses
                .filter((r: SurveyResponse) => r.additionalComments && r.additionalComments.trim())
                .map((r: SurveyResponse) => r.additionalComments as string)
              if (comments.length === 0) return null
              return (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    Comments ({comments.length})
                  </div>
                  <div className="space-y-2">
                    {comments.map((c, i) => (
                      <div key={i} className="px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700 italic">
                        "{c}"
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
          </div>
          )}

          <RisksCard
            risks={project.risks ?? []}
            currentUser={authUser ? { oid: authUser.id, name: authUser.name, email: authUser.email } : undefined}
            onSave={saveRisks}
          />
        </div>

        <div className="space-y-6">

          {/* Forecast Outlook - projected OTIFAC for in-flight projects, using the
              exact same rules as the Reports forecast so the two always agree. */}
          {project.status === 'forecast' && (() => {
            const minScore = settings?.riskForecastMinScore ?? DEFAULT_RISK_FORECAST_MIN_SCORE
            const onTimeMs = (settings?.onTimeDays ?? 14) * 24 * 60 * 60 * 1000
            const ot = forecastOnTime(project, onTimeMs)
            const material = (cat: 'delivery' | 'scope' | 'cost') =>
              (project.risks ?? []).find(r => r.status === 'open' && r.category === cat && effectiveRiskScore(r) >= minScore)
            const delivery = material('delivery')
            const scope = material('scope')
            const cost = material('cost')
            const onTimeReason =
              ot.reason === 'planned-late' ? `Forecast go-live ${project.actualGoLive ? formatDate(project.actualGoLive) : ''} is past the due date`
              : ot.reason === 'overdue' ? 'Past due date'
              : ''
            const rows = [
              { label: 'On Time', miss: ot.late || !!delivery, reasons: [onTimeReason, delivery ? `Delivery risk: ${delivery.title}` : ''].filter(Boolean) },
              { label: 'In Full', miss: !!scope, reasons: [scope ? `Scope risk: ${scope.title}` : ''].filter(Boolean) },
              { label: 'At Cost', miss: !!cost, reasons: [cost ? `Cost risk: ${cost.title}` : ''].filter(Boolean) },
            ]
            const misses = rows.filter(r => r.miss).length
            return (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-[#005EB8]/10 flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#005EB8]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-slate-800 leading-none">Forecast Outlook</h3>
                    <p className="text-[11px] text-slate-400 mt-1">Projected landing vs KPIs</p>
                  </div>
                  <span className="text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 flex-shrink-0">In-flight</span>
                </div>

                <div className={`flex items-center gap-1.5 text-xs font-semibold mb-3 px-3 py-2 rounded-lg ${
                  misses > 0 ? 'bg-red-50 text-[#EF3340]' : 'bg-emerald-50 text-emerald-700'
                }`}>
                  {misses > 0 ? <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" /> : <Check className="w-3.5 h-3.5 flex-shrink-0" />}
                  {misses === 0 ? 'On track across On Time, In Full & At Cost' : `${misses} KPI${misses > 1 ? 's' : ''} projected to miss`}
                </div>

                <div className="space-y-2.5">
                  {rows.map(r => (
                    <div key={r.label}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">{r.label}</span>
                        <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${
                          r.miss ? 'bg-red-100 text-[#EF3340]' : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {r.miss ? <><AlertTriangle className="w-3 h-3" /> At risk</> : <><Check className="w-3 h-3" /> On track</>}
                        </span>
                      </div>
                      {r.reasons.map((reason, i) => (
                        <div key={i} className="text-[11px] text-slate-400 mt-1 flex items-start gap-1">
                          <span className="text-slate-300 leading-tight">↳</span><span className="truncate">{reason}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )
          })()}

          {(() => {
            const onTimeDays = settings?.onTimeDays ?? 14
            const atCostPct = settings?.atCostPct ?? 10
            const onTimeMs = onTimeDays * 24 * 60 * 60 * 1000
            const atCostMultiplier = 1 + atCostPct / 100
            const onTime = project.actualGoLive && project.dueDate
              ? new Date(project.actualGoLive).getTime() - new Date(project.dueDate).getTime() <= onTimeMs
              : null
            const atCost = project.actualCost !== undefined && project.forecastCost > 0
              ? project.actualCost <= project.forecastCost * atCostMultiplier
              : null
            const inFull = project.deliveredInFull === 'Y' ? true : project.deliveredInFull === 'N' ? false : null
            const satisfaction = calculateAverageSatisfactionScore(responses)
            const adoption = projectAdoptionPct(project)

            const kpiRows = [
              { label: 'On Time',      value: onTime === null ? '-' : onTime ? 'Yes' : 'No',         pass: onTime },
              { label: 'At Cost',      value: atCost === null ? '-' : atCost ? 'Yes' : 'No',         pass: atCost },
              { label: 'In Full',      value: inFull === null ? '-' : inFull ? 'Yes' : 'No',         pass: inFull },
              { label: 'Satisfaction', value: satisfaction > 0 ? `${satisfaction}/5` : '-',           pass: satisfaction > 0 ? satisfaction >= 3.5 : null },
              { label: 'Adoption',     value: adoption !== null ? `${adoption}%` : '-',               pass: adoption !== null ? adoption >= 80 : null },
            ]
            return (
              <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <h3 className="font-bold text-slate-800 mb-3">Project KPIs</h3>
                <div className="space-y-2">
                  {kpiRows.map(k => (
                    <div key={k.label} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">{k.label}</span>
                      <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${
                        k.pass === null ? 'bg-slate-100 text-slate-400'
                        : k.pass ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-[#EF3340]'
                      }`}>{k.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })()}

          {/* Reporting - one row per report type this project can file.
              Opt-in per type rather than derived from tier, so re-tiering a
              project never silently changes what it owes. */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 flex items-center gap-1.5 mb-4">
              Reporting
              <InfoTooltip>
                <strong>Reporting</strong>
                <p>Which governance packs this project feeds. Only the larger projects
                  report, so each type is switched on per project.</p>
              </InfoTooltip>
            </h3>

            <div className="divide-y divide-slate-100">
              {/* Status Report - IT & Data governance slide */}
              <div className="pb-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800">Status Report</div>
                    <p className="text-xs text-slate-500 mt-0.5">IT &amp; Data governance slide</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!project.reportable}
                    aria-label="File a monthly status report"
                    onClick={() => toggleReportType('reportable', !project.reportable)}
                    className={`relative w-10 h-[22px] rounded-full shrink-0 transition-colors ${
                      project.reportable ? 'bg-[#005EB8]' : 'bg-slate-200'
                    }`}
                  >
                    <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-all ${
                      project.reportable ? 'left-[21px]' : 'left-[3px]'
                    }`} />
                  </button>
                </div>
                {project.reportable && (
                  <Link
                    to={`/projects/${project.id}/report`}
                    className="mt-3 w-full bg-[#005EB8] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#004a94] flex items-center justify-center gap-2 shadow-sm"
                  >
                    <Presentation className="w-4 h-4" />
                    Open this month’s report
                  </Link>
                )}
                {project.reportable && reportWindow && (
                  <div className="mt-2 flex justify-center">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                      reportWindow.pastDue ? 'bg-[#F88806]/10 text-[#B8640B] border-[#F88806]/30'
                        : reportWindow.isOpen ? 'bg-[#00ACED]/10 text-[#0077A8] border-[#00ACED]/30'
                          : reportWindow.notYetOpen || reportWindow.dueAt ? 'bg-slate-100 text-slate-500 border-slate-200'
                            : 'bg-[#F88806]/10 text-[#B8640B] border-[#F88806]/30'}`}>
                      <CalendarDays className="w-3.5 h-3.5" />
                      {windowChipLabel(reportWindow)}
                    </span>
                  </div>
                )}
              </div>

              {/* DDI - one row on the portfolio pack, drawn from this project's
                  monthly status report. */}
              <div className="pt-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-700">DDI</div>
                  <p className="text-xs text-slate-500 mt-0.5">Portfolio-level summary row</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!project.ddi}
                  aria-label="Include on the DDI portfolio slide"
                  onClick={() => toggleReportType('ddi', !project.ddi)}
                  className={`relative w-10 h-[22px] rounded-full shrink-0 transition-colors ${
                    project.ddi ? 'bg-[#005EB8]' : 'bg-slate-200'
                  }`}
                >
                  <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-all ${
                    project.ddi ? 'left-[21px]' : 'left-[3px]'
                  }`} />
                </button>
              </div>
              {project.ddi && (
                <Link
                  to="/ddi"
                  className="mt-3 w-full bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-slate-50 flex items-center justify-center gap-2 shadow-sm"
                >
                  <Presentation className="w-4 h-4" />
                  Open DDI Portfolio
                </Link>
              )}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-4">Pipeline Status</h3>
            <div className="space-y-3">
              {pipelineSteps.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                    step.done ? 'bg-emerald-100 text-[#00843D]' : step.warn ? 'bg-amber-100 text-[#F88806]' : step.active ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {step.done ? '✓' : step.warn ? '!' : step.active ? '◉' : '○'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${step.done ? 'text-slate-800' : step.warn ? 'text-amber-700' : step.active ? 'text-slate-800' : 'text-slate-400'}`}>{step.label}</div>
                    <div className={`text-xs break-words ${step.done || step.active ? 'text-slate-500' : step.warn ? 'text-[#F88806]' : 'text-slate-400'}`}>{step.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`bg-white rounded-2xl border p-6 ${!stakeholdersSatisfied ? 'border-amber-200 ring-1 ring-amber-200' : 'border-slate-200'}`}>
            <div className="flex items-start justify-between gap-3 mb-1">
              <h3 className="font-bold text-slate-800 flex items-center gap-1.5">
                Stakeholders
                {!stakeholdersSatisfied && <span className="w-1.5 h-1.5 bg-amber-400 rounded-full" title="Needed to continue" />}
                {skipStakeholders && <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Skipped</span>}
              </h3>
              {/* Skip toggle - back-loaded projects have no survey flow.
                  Disabled once a survey has actually been sent, to prevent
                  accidentally hiding real response data. */}
              <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer select-none" title={hasSent ? 'Cannot skip after a survey has been sent' : 'Skip survey for this project'}>
                <input
                  type="checkbox"
                  className="w-3 h-3 rounded accent-slate-500"
                  checked={skipStakeholders}
                  disabled={hasSent}
                  onChange={e => updateField('skipStakeholders', e.target.checked)}
                />
                No stakeholders
              </label>
            </div>
            {skipStakeholders ? (
              <p className="text-sm text-slate-400 py-2">Survey flow is disabled for this project. Satisfaction KPI excludes it.</p>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  {project.stakeholders.map(email => {
                    const wasSent = (project.surveyEmailsSent ?? []).includes(email)
                    const isLoadingThis = sendingEmail === email
                    return (
                      <div key={email} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm text-slate-700 truncate">{email}</span>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                          {hasSent && !wasSent && (
                            <button onClick={() => handleSendToOne(email)} disabled={!!sendingEmail}
                              className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-50 transition-colors">
                              {isLoadingThis ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                              {isLoadingThis ? 'Sending…' : 'Not sent'}
                            </button>
                          )}
                          <button onClick={() => removeStakeholder(email)} className="text-slate-400 hover:text-red-500">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                  {project.stakeholders.length === 0 && <p className="text-sm text-slate-400 text-center py-2">No stakeholders yet</p>}
                </div>
                <div className="flex gap-2">
                  <input type="email" placeholder="Add email..." value={newEmail} onChange={e => setNewEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addStakeholder()}
                    className="flex-1 min-w-0 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" />
                  <button onClick={addStakeholder} className="flex-shrink-0 px-3 py-2 bg-slate-100 text-slate-600 rounded-lg text-sm font-semibold hover:bg-slate-200 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add
                  </button>
                </div>
              </>
            )}
          </div>

          {project.asanaProjectId && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-4">
                <img src="https://cdn.jsdelivr.net/gh/homarr-labs/dashboard-icons/svg/asana.svg" alt="Asana" className="w-6 h-6" />
                <h3 className="font-bold text-slate-800">Asana Sync</h3>
              </div>
              {project.lastSyncedAt && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Last synced</div>
                      <div className="text-sm font-bold text-slate-800">{formatDateTime(project.lastSyncedAt)}</div>
                    </div>
                    <div className="w-2 h-2 bg-[#00843D] rounded-full animate-pulse" />
                  </div>
                </div>
              )}
              <button onClick={handleSync} disabled={syncing || asanaConnected === false}
                className="w-full px-4 py-2.5 bg-[#005EB8] text-white rounded-lg text-sm font-semibold hover:bg-[#004a94] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {syncing ? 'Syncing...' : 'Sync Now'}
              </button>
              {asanaConnected === false ? (
                <p className="text-xs text-slate-500 text-center mt-2">
                  Not connected to Asana. <Link to="/settings" className="text-[#005EB8] font-semibold hover:underline">Connect in Settings</Link>
                </p>
              ) : (
                <p className="text-xs text-slate-500 text-center mt-2">Pull latest data from Asana</p>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-bold text-slate-800 mb-3">Survey</h3>
            {skipStakeholders ? (
              <div className="flex items-start gap-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                <Clock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-slate-500">
                  <span className="font-semibold">Survey skipped.</span> Untick "No stakeholders" above to re-enable.
                </div>
              </div>
            ) : !configDone ? (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Clock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-700">
                  <span className="font-semibold">Complete configuration first:</span>{' '}
                  {missingFields.length > 0
                    ? `Missing ${missingFields.join(', ')}`
                    : 'Add at least one stakeholder'}
                </div>
              </div>
            ) : !project.actualGoLive ? (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Clock className="w-4 h-4" />
                <span>Set a go-live date first</span>
              </div>
            ) : (
              <div>
                {hasSent && (
                  <p className="text-xs text-[#00843D] font-medium mb-3">Sent {formatDateTime(project.surveySentAt!)}</p>
                )}
                {(() => {
                  const notResponded = Math.max(0, project.stakeholders.length - responseCount)
                  return (
                    <button onClick={handleSendSurvey} disabled={sending || (hasSent && notResponded === 0)}
                      className="w-full px-4 py-2.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg text-sm font-semibold hover:bg-emerald-100 flex items-center justify-center gap-2 disabled:opacity-50">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                      {sending ? 'Sending...' : hasSent ? `Resend to ${notResponded} ${notResponded === 1 ? 'person' : 'people'}` : 'Send Survey'}
                    </button>
                  )
                })()}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Monthly Hours Ledger - shown for complete projects or projects with existing data. */}
      {hasLedger && (
        <>
          {/* Ledger review modal */}
          {ledgerReview && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                  <h3 className="font-bold text-slate-800">Review ledger changes</h3>
                  <button onClick={() => { setLedgerReview(null); setLedgerNote('') }} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-4 max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-100"><th className="text-left py-1 text-xs font-bold text-slate-500">Month</th><th className="text-right py-1 text-xs font-bold text-slate-500">Before</th><th className="text-right py-1 text-xs font-bold text-slate-500">After</th><th className="text-right py-1 text-xs font-bold text-slate-500">Δ</th></tr></thead>
                    <tbody>
                      {ledgerReview.map(r => (
                        <tr key={r.key} className="border-b border-slate-50"><td className="py-1.5 text-slate-700">{r.monthLabel}</td><td className="py-1.5 text-right tabular-nums text-slate-500">{formatNumber(r.before)}</td><td className="py-1.5 text-right tabular-nums font-semibold">{formatNumber(r.after)}</td><td className={`py-1.5 text-right tabular-nums font-semibold ${r.delta > 0 ? 'text-[#00843D]' : 'text-[#EF3340]'}`}>{r.delta > 0 ? '+' : ''}{formatNumber(r.delta)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-6 py-3 border-t border-slate-100">
                  <input type="text" value={ledgerNote} onChange={e => setLedgerNote(e.target.value)} placeholder="Why? (optional note)" className={`${inputClass} w-full`} />
                </div>
                <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-3">
                  <button onClick={() => { setLedgerReview(null); setLedgerNote('') }} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200">Back</button>
                  <button onClick={confirmLedgerSave} disabled={savingLedger} className="btn-primary text-white px-4 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
                    {savingLedger ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {savingLedger ? 'Saving…' : 'Confirm & save'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Hours summary tile */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Hrs Saved YTD</div>
              <div className="text-2xl font-bold text-slate-800 mt-1">{formatNumber(Math.round(ytdHrs))}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Forecast {new Date().getFullYear()}</div>
              <div className="text-2xl font-bold text-slate-800 mt-1">{formatNumber(Math.round(thisYearHrs))}</div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Lifetime</div>
              <div className="text-2xl font-bold text-slate-800 mt-1">{formatNumber(Math.round(lifetimeHrs))}</div>
            </div>
          </div>

          {/* Ledger table */}
          <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h3 className="font-bold text-slate-800">Monthly Hours Ledger</h3>
                <p className="text-xs text-slate-500 mt-0.5">Base is auto-seeded from the forecast on completion. Overwrite any row to record what actually happened.</p>
              </div>
              <div className="flex items-center gap-2">
                {!ledgerEditing && (
                  <>
                    <button onClick={() => setRefreshConfirm(true)} title="Re-derive forecast years from the latest prior-year total" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200">
                      <RefreshCw className="w-3.5 h-3.5" /> Refresh forecast
                    </button>
                    <button onClick={startLedgerEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200">
                      <Pencil className="w-3.5 h-3.5" /> Edit ledger
                    </button>
                  </>
                )}
                {ledgerEditing && (
                  <>
                    <button onClick={cancelLedgerEdit} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200">Cancel</button>
                    <button onClick={openReview} className="btn-primary text-white px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5" /> Review changes
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Year tabs */}
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-1">
                {years.map(y => (
                  <button key={y} onClick={() => setLedgerYear(y)} className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors ${y === activeYear ? 'bg-[#005EB8] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>{y}</button>
                ))}
              </div>
              <div className="text-xs text-slate-500"><span className="font-semibold text-slate-700">{formatNumber(Math.round(activeYearTotal))} hrs</span> for {activeYear}</div>
            </div>

            {/* Month table */}
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-5 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">Month</th>
                  <th className="px-5 py-2 text-right text-xs font-bold text-slate-600 uppercase tracking-wide">Hours saved</th>
                  <th className="px-5 py-2 text-right text-xs font-bold text-slate-600 uppercase tracking-wide">vs expected</th>
                  <th className="px-5 py-2 text-left text-xs font-bold text-slate-600 uppercase tracking-wide">Last note</th>
                </tr>
              </thead>
              <tbody>
                {monthRows.map(row => {
                  const displayTotal = Math.round(row.total)
                  const displayDelta = Math.round(row.deltaVsBase)
                  const displayBase = Math.round(row.base)
                  return (
                    <tr key={row.key} className={`border-b border-slate-50 last:border-0 ${row.dirty ? 'bg-amber-50/50' : !row.isActual ? 'bg-slate-50/60' : ''}`}>
                      <td className={`px-5 py-3 font-semibold ${row.isActual ? 'text-slate-700' : 'text-slate-400 italic'}`}>
                        {row.label}
                        {!row.isActual && <span className="ml-1.5 text-[10px] font-normal not-italic text-slate-400">forecast</span>}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums">
                        {ledgerEditing ? (
                          <input type="number" step="1" value={editBuffer[row.key] ?? displayTotal} onChange={e => setTotalFor(row.key, e.target.value)} className={`${inputClass} text-right w-28 inline-block`} />
                        ) : (
                          <span className="text-sm font-semibold text-slate-800">{displayTotal > 0 ? formatNumber(displayTotal) : <span className="text-slate-300 font-normal">-</span>}</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right text-xs tabular-nums">
                        {row.base === 0 && displayDelta === 0 ? (
                          <span className="text-slate-300">-</span>
                        ) : displayDelta === 0 ? (
                          <span className="text-slate-400">on forecast</span>
                        ) : (
                          <span title={`Expected ${formatNumber(displayBase)} hrs from forecast`} className={displayDelta > 0 ? 'text-[#00843D] font-medium' : 'text-[#EF3340] font-medium'}>
                            {displayDelta > 0 ? '+' : ''}{formatNumber(displayDelta)}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-500 max-w-xs">
                        {row.notes.length > 0 ? <span title={row.notes.join(' · ')} className="line-clamp-1">{row.notes[row.notes.length - 1]}</span> : <span className="text-slate-300">-</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Monthly trend chart */}
          <div className="mt-4 mb-2">
            <ChartCard icon={TrendingUp} iconGrad="from-[#005EB8] to-[#005EB8] text-white" title={`Monthly Hours Saved (${activeYear})`} subtitle="Each point is the sum of base + adjustments for that month">
              {activeYearTotal === 0 ? (
                <div className="h-52 flex items-center justify-center text-sm text-slate-400">No hours recorded for {activeYear} yet</div>
              ) : (
                <ChartContainer config={trendConfig} className="aspect-auto h-52 w-full">
                  <LineChart data={trendData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="4 4" />
                    <XAxis dataKey="month" tickLine={false} axisLine={false} tick={AXIS_TICK} />
                    <YAxis tickLine={false} axisLine={false} tick={AXIS_TICK} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line type="monotone" dataKey="hrs" stroke={CHART_COLORS.cyan} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ChartContainer>
              )}
            </ChartCard>
          </div>
        </>
      )}

      {/* Activity / audit trail - collapsed by default, compliance only.
          SYSTEM-actor entries (Asana sync writes) hidden behind a toggle. */}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setAuditOpen(o => !o)}
          className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <History className="w-4 h-4 text-slate-400" />
            <div className="flex items-baseline gap-2">
              <h3 className="font-bold text-slate-800 text-sm">Activity</h3>
              <span className="text-xs text-slate-400">{(project.auditTrail || []).length} entries</span>
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${auditOpen ? 'rotate-180' : ''}`} />
        </button>
        {auditOpen && (
          <div className="border-t border-slate-100 px-6 py-4">
            <div className="flex items-center justify-end mb-3">
              <label className="flex items-center gap-2 text-xs text-slate-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showSystemAudit}
                  onChange={e => setShowSystemAudit(e.target.checked)}
                  className="rounded border-slate-300"
                />
                Show system changes
              </label>
            </div>
            {(() => {
              const entries: AuditEntry[] = (project.auditTrail || [])
                .filter(e => showSystemAudit || e.actor.oid !== 'SYSTEM')
                .slice()
                .reverse()
              if (entries.length === 0) {
                return <p className="text-sm text-slate-400 text-center py-4">No activity recorded</p>
              }
              return (
                <div className="space-y-2">
                  {entries.map((entry, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm py-2 border-b border-slate-50 last:border-0">
                      <div className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                        {entry.actor.oid === 'SYSTEM' ? 'SY' : getInitials(entry.actor.email || entry.actor.name || 'U')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-slate-700">
                          <span className="font-semibold">{entry.actor.name || entry.actor.email || 'Unknown'}</span>
                          {' '}changed{' '}
                          <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{entry.field}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 break-words">
                          <span className="line-through text-slate-400">{JSON.stringify(entry.before) ?? '-'}</span>
                          {' → '}
                          <span>{JSON.stringify(entry.after) ?? '-'}</span>
                        </div>
                      </div>
                      <div className="text-xs text-slate-400 flex-shrink-0">{formatDateTime(entry.at)}</div>
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>
        )}
      </div>
    </div>
  )
}
